import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { get } from "@/lib/api";
import type { SearchResults } from "@/components/search/SearchPalette";
import {
  FileText,
  StickyNote,
  Quote,
  BookOpen,
  Tag,
  Search as SearchIcon,
} from "lucide-react";

export default function SearchPage() {
  const search = useSearch();
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const initialQ = params.get("q") || "";
  const initialTag = params.get("tag") || "";

  const [query, setQuery] = useState(initialQ || (initialTag ? `#${initialTag}` : ""));
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setQuery(initialQ || (initialTag ? `#${initialTag}` : ""));
  }, [initialQ, initialTag]);

  useEffect(() => {
    const run = async () => {
      const q = initialQ.trim();
      const tag = initialTag.trim().replace(/^#/, "");
      if (!q && !tag) {
        setResults(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const qs = q
          ? `q=${encodeURIComponent(q)}`
          : `tag=${encodeURIComponent(tag)}`;
        const data = await get<SearchResults>(`/search?${qs}`);
        setResults(data);
      } catch (err) {
        console.error(err);
        setError("Search failed. Please try again.");
        setResults(null);
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [initialQ, initialTag]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim().replace(/^#/, "");
    if (!q) return;
    // If user typed a bare tag-looking query from tag deep link style
    window.location.href = `/search?q=${encodeURIComponent(query.trim())}`;
  };

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-3xl py-8 px-6">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Search</h1>
        <p className="text-muted-foreground mb-6">
          Find documents, notes, quotes, scripture, and tags.
        </p>

        <form onSubmit={submit} className="relative mb-8">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your workspace…"
            autoFocus
          />
        </form>

        {loading && (
          <p className="text-sm text-muted-foreground">Searching…</p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {results && (
          <div className="space-y-8">
            <ResultSection
              title="Documents"
              icon={<FileText className="h-4 w-4" />}
              empty={results.documents.length === 0}
            >
              {results.documents.map((doc) => (
                <Link key={doc.id} href={`/documents/${doc.id}/edit`}>
                  <div className="rounded-lg border px-4 py-3 hover:bg-muted/40 cursor-pointer">
                    <p className="font-medium">{doc.title || "Untitled"}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {doc.content}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(doc.updated_at), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </Link>
              ))}
            </ResultSection>

            <ResultSection
              title="Notes"
              icon={<StickyNote className="h-4 w-4" />}
              empty={results.notes.length === 0}
            >
              {results.notes.map((note) => (
                <div key={note.id} className="rounded-lg border px-4 py-3">
                  <Markdown content={note.content} className="text-sm" />
                  <p className="text-xs text-muted-foreground mt-1 capitalize">
                    {note.parent_type}
                    {note.parent_type === "document" && note.parent_id ? (
                      <>
                        {" · "}
                        <Link href={`/documents/${note.parent_id}/edit`}>
                          <Button variant="link" className="h-auto p-0 text-xs">
                            Open document
                          </Button>
                        </Link>
                      </>
                    ) : null}
                  </p>
                </div>
              ))}
            </ResultSection>

            <ResultSection
              title="Quotes"
              icon={<Quote className="h-4 w-4" />}
              empty={results.quotes.length === 0}
            >
              {results.quotes.map((quote) => (
                <Link key={quote.id} href={`/quotes/${quote.id}`}>
                  <div className="rounded-lg border px-4 py-3 hover:bg-muted/40 cursor-pointer">
                    <p className="text-sm">
                      "<Markdown content={quote.content} inline />"
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      — {quote.author}
                    </p>
                  </div>
                </Link>
              ))}
            </ResultSection>

            <ResultSection
              title="Scripture"
              icon={<BookOpen className="h-4 w-4" />}
              empty={results.scripture.length === 0}
            >
              {results.scripture.map((ref) => (
                <Link key={ref.id} href={`/scripture-references/${ref.id}`}>
                  <div className="rounded-lg border px-4 py-3 hover:bg-muted/40 cursor-pointer">
                    <p className="font-medium">
                      {ref.book} {ref.chapter}:{ref.verse_start}
                      {ref.verse_end > ref.verse_start
                        ? `-${ref.verse_end}`
                        : ""}
                    </p>
                    {ref.text && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {ref.text}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </ResultSection>

            <ResultSection
              title="Tags"
              icon={<Tag className="h-4 w-4" />}
              empty={results.tags.length === 0}
            >
              <div className="flex flex-wrap gap-2">
                {results.tags.map((tag) => (
                  <Link key={tag.id} href={`/tags/${tag.id}`}>
                    <Button variant="outline" size="sm">
                      #{tag.name}
                    </Button>
                  </Link>
                ))}
              </div>
            </ResultSection>
          </div>
        )}

        {!loading && !results && !error && (
          <p className="text-sm text-muted-foreground">
            Enter a query, or press ⌘K / Ctrl+K anywhere.
          </p>
        )}
      </div>
    </AppLayout>
  );
}

function ResultSection({
  title,
  icon,
  empty,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {empty ? (
        <p className="text-sm text-muted-foreground">No matches.</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </section>
  );
}
